import React, { useState } from 'react';
import styled from 'styled-components';
import { useTheme } from 'hooks';
import { Camera } from 'assets';
import { Button, Portal } from 'components/partials';
import { TRANSLATIONS_VALUES_KEYS } from 'redux/internationalization/internationalization';

import { makePatch, useTranslate } from 'services';
import { useDispatch, useSelector } from 'react-redux';
import { IPhotoSeries } from 'redux/photoseries/photoseries';

import {
  selectPublicUserTrackingInfo,
  getPhotoSeries
} from 'redux/root.selectors';

import {
  setPublicUserTrackingInfo
} from 'redux/root.actions';
import { invalidVinErrorMessage } from '../error-modal/error-modal.component';

const { vehicle_scan, validation_wrapper, user_feedback } = TRANSLATIONS_VALUES_KEYS;

const VinModalWrapper = styled.div<{ background: string }>`
  background: ${({ background }) => background};
  backdrop-filter: blur(24px);
  position: absolute;
  z-index: 900000;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  margin: 0 auto;
  width: 100%;
  min-height: var(--view-height);
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  -ms-overflow-style: none;
  scrollbar-width: none;

  @media screen and (min-aspect-ratio: 13/9) {
    margin-top: var(--page-margin-top);
  }

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-aspect-ratio: 13/9) {
    transform: rotate(90deg);
    transform-origin: left top;
    min-width: var(--view-height);
    max-width: var(--view-height);
    left: 100vw;
    min-height: 100vw;
    max-height: 100vw;
  }

  & p {
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1.1875rem;
  }

  & .icon > * {
    justify-content: center;
  }
`;

const Heading = styled.h1`
  font-size: 1.75rem;
  width: 90%;
  margin: 0 auto;
  padding: 2rem 0 1rem 0;
  text-align: left;
`;

const ButtonWrapper = styled.div<{ buttonMinWidth: string }>`
  display: flex;
  justify-content: center;
  width: 90%;
  margin: 0 auto;
  padding: 1.875rem 0;

  & > button {
    min-width: ${({ buttonMinWidth }) => buttonMinWidth};
    max-width: 15.625rem !important;
    margin: 0 0.625rem;
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const VinInputWrapper = styled.div`
  width: 90%;
  margin: 0 auto;
  padding: 2rem 0;
  position: relative;
`;

const VinInput = styled.input<{ color: string, background: string, border: string, hasError: boolean }>`
  && {
    background: ${({ background }) => background};
    border: 1px solid ${({ border, hasError }) => hasError ? '#e74c3c' : border};
    height: 4rem;
    border-radius: 0.625rem;
    text-align: center;
    font-size: 1.8rem !important;
    text-transform: uppercase;
    font-weight: 700;
    letter-spacing: 0.375rem;
    color: ${({ color }) => color};
    width: 100%;
  }

  &::placeholder {
    letter-spacing: normal;
    text-transform: none;
  }

  &:focus {
    outline: none;
  }
`;

const ErrorMessage = styled.p`
  color: #e74c3c;
  font-size: 0.875rem !important;
  text-align: center;
  margin-top: 0.5rem;
  position: absolute;
  width: 100%;
  left: 0;
  font-weight: 700 !important;
`;

export type VinModalProps = {
  onRetake: React.MouseEventHandler<HTMLButtonElement>;
  shownModal: boolean;
  onContinue?: React.MouseEventHandler<HTMLButtonElement>;
};

export function VinModal(props: VinModalProps) {
  const {
    shownModal, onRetake, onContinue
  } = props;
  const publicUserTrackingInfo = useSelector(selectPublicUserTrackingInfo);
  const photoSeries: IPhotoSeries = useSelector(getPhotoSeries);

  const { styles } = useTheme();
  const { translate, translateToString } = useTranslate();
  const dispatch = useDispatch();

  const [vinCode, setVinCode] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<React.ReactNode>(null);

  const secondaryButtonProps = {
    bg: styles.colorSecondaryButtonBackground,
    color: styles.colorSecondaryButtonText,
    hoverBorderColor: styles.colorSecondaryButtonBackgroundDark,
    hoverShadowColor: styles.colorSecondaryButtonBackgroundLight,
  };

  const handleVinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    setVinCode(value);
    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleConfirm = () => {
    makePatch(`capture/${photoSeries.id}/vin`, {
      vin: vinCode
    }).subscribe(
      () => {
        // updating tracking information
        const newPublicUserTrackingInfo = {
          ...publicUserTrackingInfo,
        };

        newPublicUserTrackingInfo.photoSeries.vehicle.vin = vinCode;
        dispatch(setPublicUserTrackingInfo(newPublicUserTrackingInfo));

        if (onContinue) {
          onContinue({} as React.MouseEvent<HTMLButtonElement>);
        }
      },
      (error: any) => {
        const errorCode = error.response.error;
        const errorMessage = (
          <>
            {invalidVinErrorMessage(translate, errorCode, false)} {translate(vehicle_scan.insert_valid_vin_text)}
          </>
        );
        setErrorMessage(errorMessage);
      }
    );
  };

  if (!shownModal) return null;

  return (
    <Portal target=".smartscan-fullscreen" direction="end">
      <VinModalWrapper className="wrapper page" background={styles.colorBackgroundGradient}>
        <Heading>{translate(vehicle_scan.enter_vin_manually_text)}</Heading>
        <Row>
          <VinInputWrapper>
            <VinInput
              name="vinCode"
              type="text"
              spellCheck="false"
              onChange={handleVinChange}
              value={vinCode}
              background={styles.colorBackgroundSurface}
              border={styles.colorBackgroundRaised}
              color={styles.colorParagraph}
              hasError={!!errorMessage}
              maxLength={17}
              placeholder={translateToString(user_feedback.input_placeholder_default)}
            />
            {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
          </VinInputWrapper>
          <ButtonWrapper buttonMinWidth="48%">
            <Button
              {...secondaryButtonProps}
              type="button"
              testId="tryAgainBtn"
              onClick={onRetake}
            >
              <Camera fill={styles.colorSecondaryButtonText} />
              &nbsp;
              {translate(validation_wrapper.try_again_text)}
            </Button>
            <Button
              type="button"
              testId="confirmBtn"
              onClick={handleConfirm}
              disabled={vinCode.length === 0}
              animate
            >
              {translate(vehicle_scan.confirm_btn_text)}
            </Button>
          </ButtonWrapper>
        </Row>
      </VinModalWrapper>
    </Portal>
  );
}
